import { TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { Formatters, Messages } from '../../../codegen/Localize';
import { ISanitizer, sanitize } from '../../../commonInfra/utils/Sanitize';
import { classes, cssClass, flexColumnStyle, flexRowStyle } from '../../../style';
import { TextileHelper } from '../../../ts/helpers/TextileHelper';
import { CommonStyles } from '../CommonStyles';
import { FormattingHints } from './FormattingHints';

/**
 * This is a React refactor of the EditableTextarea
 * It uses the Textile text formatting which supports some markup, used currently for descriptions.
 */

const cssPrefix = 'editableDescription';

const rootClass = cssClass(cssPrefix, 'root', flexRowStyle);

const buttonContainer = cssClass(cssPrefix, 'buttonContainer', flexColumnStyle, {
  textAlign: 'center',
  marginLeft: CommonStyles.Sizes.LargeSpace,
  width: '135px'
});

const saveButtonDisabled: React.CSSProperties = {
  backgroundColor: TabStyles.Colors.GoDarkDisabled,
  borderColor: TabStyles.Colors.GoDarkDisabled,
  pointerEvents: 'none',
  userSelect: 'none',
  'msUserSelect': 'none',
  'MozUserSelect': 'none',
  'WebkitUserSelect': 'none'
};

const cancelButton = cssClass(cssPrefix, 'cancelButton', {
  marginTop: CommonStyles.Sizes.DefaultSpace
});

const staticText = cssClass(cssPrefix, 'staticText', {
  width: 500,
  boxSizing: 'border-box',
  padding: 7
});

const textAreaClass = cssClass(cssPrefix, 'textarea', {
  width: 500,
  boxSizing: 'border-box'
});

const defaultMaxLength: number = 50000;

const characterCounter = cssClass(cssPrefix, 'characterCounter', {
  float: 'right'
});

const overLimitCounter = cssClass(cssPrefix, 'overLimitCounter', {
  color: TabStyles.Colors.AttentionLight
});

export type EditableDescriptionProps = {
  readonly originalText: string;
  readonly customMaxCharacterLength?: number;
  readonly startInEditMode?: boolean;
  readonly cancel?: () => void;
  readonly onSubmit: (text: string) => void;
};

export enum ViewType {
  view,
  preview,
  edit
}

export type EditableDescriptionState = {
  readonly textile: string;
  readonly mode: ViewType;
};

export class EditableDescription extends React.PureComponent<EditableDescriptionProps, EditableDescriptionState> {

  private textAreaRef: HTMLTextAreaElement;
  private readonly sanitize: ISanitizer;

  constructor(props: EditableDescriptionProps) {
    super(props);
    this.sanitize = sanitize;
    this.state = {
      textile: this.props.originalText ? this.props.originalText : '',
      mode: this.props.startInEditMode ?  ViewType.edit : ViewType.view,
    };
  }

  private startEditing = () => {
    this.setState({ mode: ViewType.edit});
  }

  private startPreview = () => {
    this.setState({ textile: this.textAreaRef.value, mode: ViewType.preview});
  }

  private save = () => {
    this.setState({ textile: this.textAreaRef && this.textAreaRef.value ? this.textAreaRef.value : this.state.textile, mode: ViewType.view});
    this.props.onSubmit(this.state.textile);
  }

  private cancel = () => {
    this.props.cancel && this.props.cancel();
    this.setState({ mode: ViewType.view });
  }

  private updateTextile = () => {
    this.setState({ textile: this.textAreaRef.value});
  }

  private renderEditAndPreviewButtons(): JSX.Element {
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return this.state.mode === ViewType.view || this.state.mode === ViewType.preview ?
      <span onClick={this.startEditing} tb-test-id="btnEdit" className={'tb-outline-button ng-scope'}>{Messages.editDescription()}</span>
    : <span onClick={this.startPreview} tb-test-id="btnPreview" className={'tb-outline-button ng-scope'}>{Messages.preview()}</span>;
  }

  private renderSaveAndCancelButtons(): JSX.Element {
    const saveButtonStyle = this.state.textile.length > (this.props.customMaxCharacterLength || defaultMaxLength) ? saveButtonDisabled : null;

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return this.state.mode === ViewType.edit || this.state.mode === ViewType.preview ?
      <React.Fragment>
        <span onClick={this.save} tb-test-id="btnSave" className={classes('tb-outline-button', 'tb-go-button', 'tb-margin-top-wide')} style={saveButtonStyle}>{Messages.save()}</span>
        <span onClick={this.cancel} tb-test-id="btnCancel" className={classes(cancelButton, 'tb-outline-button', 'tb-cancel-button')}>{Messages.cancel()}</span>
      </React.Fragment> : null;
  }

  private renderButtons(): JSX.Element {
    return (
      <div className={classes(buttonContainer, 'tb-editable-textarea-buttons')}>
        {this.renderEditAndPreviewButtons()}
        {this.renderSaveAndCancelButtons()}
      </div>
    );
  }

  private renderCharacterCounter(): JSX.Element {
    return (
      <div className={classes(characterCounter, 'tb-character-counter', this.state.textile.length > (this.props.customMaxCharacterLength || defaultMaxLength) && overLimitCounter)}>
        {Messages.characterCounter({COUNT_LOC: Formatters.formatCount(this.state.textile.length), MAXIMUM_LOC: Formatters.formatCount(this.props.customMaxCharacterLength || defaultMaxLength)})}
      </div>
    );
  }

  private setupTextareaRef = (textarea: HTMLTextAreaElement) => this.textAreaRef = textarea;

  private renderEditView(): JSX.Element {
    return (
      <div className={'tb-editable-textarea'}>
        <textarea onChange={this.updateTextile} ref={this.setupTextareaRef} className={classes(textAreaClass, 'tb-textile-editor-textarea')}>{this.state.textile}</textarea>
        <div>
          {this.renderCharacterCounter()}
          <FormattingHints/>
        </div>
      </div>
    );
  }

  private renderDescription(): JSX.Element {
    return this.state.mode === ViewType.view || this.state.mode === ViewType.preview ?
      // we know this function sanitizes text so suppress the error
      // eslint-disable-next-line jam3/no-sanitizer-with-danger
      <div className={classes(staticText, 'tb-textile', 'tb-enable-selection')} dangerouslySetInnerHTML={{__html: TextileHelper.getSanitizedHtmlFromTextile(this.state.textile, this.sanitize)}}/>
      : this.renderEditView();
  }

  public render(): JSX.Element {
    return (
      <div className={rootClass}>
        <div className={'tb-details-value'}>
          {this.renderDescription()}
        </div>
        <div className={'tb-about-actions tb-details-button-cell'}>
          {/* TODO: if is not read only*/ this.renderButtons()}
        </div>
      </div>
    );
  }
}
